import React, { useMemo } from 'react';
import {
  CompassCardComponent,
  CompassCardComponentProps,
} from 'react-scale/corp/views/DataCompassV2/types';
import { Code, Flex, Icon, Text } from '@scale/scaleui-radix';
import { useCompassCard } from '@scale/compass-ui/store';
import { columnsFromRecord } from '@scale/compass-shared/utils';
import { faCheckCircle } from '@fortawesome/pro-regular-svg-icons';

const SquadActionsCardComponent: CompassCardComponent = ({ index }: CompassCardComponentProps) => {
  const { retrievedOutput: priorCardRetrievedOutput } = useCompassCard(index - 1);

  const columnError = useMemo(() => {
    try {
      const columns = columnsFromRecord(priorCardRetrievedOutput?.response?.data);
      const userIdCol = columns.find(col => col.header.toLowerCase() === 'user_id');
      const actionCol = columns.find(col => col.header.toLowerCase() === 'action');

      if (!userIdCol || !actionCol) {
        return `No 'user_id' or 'action' column found.`;
      }

      return null;
    } catch (error) {
      return 'Error processing columns from prior card.';
    }
  }, [priorCardRetrievedOutput]);

  if (columnError) {
    return <Text color="neutral">{columnError}</Text>;
  }

  return (
    <Flex direction="column" gap="4">
      <Text color="neutral">
        Running this card will apply the squad action to the user in each row of the prior card.
      </Text>

      <Flex gap="2" align="center">
        <Icon icon={faCheckCircle} label="check" color="success" />
        <Text color="neutral">
          Using column <Code>user_id</Code> to identify User ID.
        </Text>
      </Flex>

      <Flex gap="2" align="center">
        <Icon icon={faCheckCircle} label="check" color="success" />
        <Text color="neutral">
          Using column <Code>squad_id</Code> to identify Squad ID (optional, used for add actions
          but not for remove actions).
        </Text>
      </Flex>

      <Flex gap="2" align="center">
        <Icon icon={faCheckCircle} label="check" color="success" />
        <Text color="neutral">
          Using column <Code>action</Code> to identify the action to perform.
        </Text>
      </Flex>

      <Flex gap="2" align="center">
        <Icon icon={faCheckCircle} label="check" color="success" />
        <Text color="neutral">
          If the action is <Code>add</Code>, the user will be added to the specified squad, and
          removed from their current squad, if any. This will fail if the user is a squad reviewer,
          or if they are already in the specified squad.
        </Text>
      </Flex>

      <Flex gap="2" align="center">
        <Icon icon={faCheckCircle} label="check" color="success" />
        <Text color="neutral">
          If the action is <Code>remove</Code>, the user will be removed from their current squad,
          if any. This will fail if the user is a squad reviewer, or if they are not currently in a
          squad.
        </Text>
      </Flex>
    </Flex>
  );
};

export default React.memo(SquadActionsCardComponent);
